import React from "react";
import { createRoot, type Root } from "react-dom/client";
import tailwindCss from "./styles/tailwind.css?inline";
import widgetCss from "./styles/widget.css?inline";
import { App } from "./App";
import { LiveDataSource } from "./data/live-data-source";
import type { WidgetDataSource } from "./data/types";

const DEFAULT_FONT_CSS_URL = "https://fonts.googleapis.com/css2?family=Poppins:wght@100;200;300;400;500&display=swap";

interface WidgetMountConfig {
  mode?: "mock" | "live";
  apiBase?: string;
  /** Required when mode is mock (e.g. dev harness injects MockDataSource). */
  dataSource?: WidgetDataSource;
  fontCssUrl?: string;
  disableDefaultFontLoading?: boolean;
}

declare global {
  interface Window {
    CSafeParcelWidget: {
      mount: (selector: string, config?: WidgetMountConfig) => void;
      unmount: (selector: string) => void;
    };
  }
}

const roots = new Map<string, Root>();
const focusGuards = new Map<string, () => void>();
const HOST_FOCUS_RESET_STYLE_ID = "csw-host-focus-reset-style";
const scheduleMicrotask =
  typeof queueMicrotask === "function"
    ? queueMicrotask
    : (callback: () => void) => Promise.resolve().then(callback);

function ensureHostFocusResetStyles(): void {
  if (document.getElementById(HOST_FOCUS_RESET_STYLE_ID)) {
    return;
  }

  const style = document.createElement("style");
  style.id = HOST_FOCUS_RESET_STYLE_ID;
  style.textContent = `
    .csafe-parcel-widget-host:focus,
    .csafe-parcel-widget-host:focus-visible,
    .csafe-parcel-widget-host:focus-within,
    #csafe-parcel-widget:focus,
    #csafe-parcel-widget:focus-visible,
    #csafe-parcel-widget:focus-within {
      outline: none !important;
      box-shadow: none !important;
    }
  `;
  document.head.appendChild(style);
}

function isFocusableControl(target: EventTarget | null): target is HTMLElement {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  if (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    target instanceof HTMLButtonElement
  ) {
    return !target.disabled;
  }

  if (target instanceof HTMLAnchorElement) {
    return target.hasAttribute("href");
  }

  return target.tabIndex >= 0;
}

function getFocusableControl(event: Event): HTMLElement | null {
  const path = typeof event.composedPath === "function" ? event.composedPath() : [event.target];

  for (const entry of path) {
    if (isFocusableControl(entry)) {
      return entry;
    }

    if (entry instanceof HTMLLabelElement) {
      const nestedControl = entry.querySelector<HTMLElement>("input, textarea, select, button");
      if (nestedControl && isFocusableControl(nestedControl)) {
        return nestedControl;
      }
    }
  }

  return null;
}

function focusElement(element: HTMLElement): void {
  if (!element.isConnected) {
    return;
  }

  try {
    element.focus({ preventScroll: true });
  } catch {
    element.focus();
  }
}

function installFocusGuard(shadowRoot: ShadowRoot): () => void {
  let recentControl: HTMLElement | null = null;
  let clearRecentControlTimeout: ReturnType<typeof setTimeout> | null = null;

  const clearRecentControl = (): void => {
    recentControl = null;
    if (clearRecentControlTimeout !== null) {
      clearTimeout(clearRecentControlTimeout);
      clearRecentControlTimeout = null;
    }
  };

  const setRecentControl = (control: HTMLElement): void => {
    recentControl = control;
    if (clearRecentControlTimeout !== null) {
      clearTimeout(clearRecentControlTimeout);
    }
    clearRecentControlTimeout = setTimeout(() => {
      recentControl = null;
      clearRecentControlTimeout = null;
    }, 500);
  };

  const resolveRecentControl = (event: Event): HTMLElement | null => {
    const control = getFocusableControl(event);
    if (!control) {
      return recentControl;
    }
    setRecentControl(control);
    return control;
  };

  const restoreRecentControlFocus = (event: Event): void => {
    const control = resolveRecentControl(event);
    if (!control) {
      return;
    }

    scheduleMicrotask(() => {
      focusElement(control);
    });
  };

  const onFocusIn = (event: Event): void => {
    if (!recentControl || !recentControl.isConnected) {
      return;
    }

    const target = event.target;
    if (target === recentControl) {
      return;
    }

    const shadowActiveElement = shadowRoot.activeElement;
    if (shadowActiveElement === recentControl) {
      return;
    }

    const control = recentControl;
    scheduleMicrotask(() => {
      if (control) {
        focusElement(control);
      }
    });
  };

  shadowRoot.addEventListener("pointerdown", restoreRecentControlFocus, true);
  shadowRoot.addEventListener("click", restoreRecentControlFocus, true);
  shadowRoot.addEventListener("focusin", onFocusIn, true);

  return () => {
    shadowRoot.removeEventListener("pointerdown", restoreRecentControlFocus, true);
    shadowRoot.removeEventListener("click", restoreRecentControlFocus, true);
    shadowRoot.removeEventListener("focusin", onFocusIn, true);
    clearRecentControl();
  };
}

function safeToken(input: string): string {
  return input.replace(/[^a-zA-Z0-9_-]/g, "_");
}

function ensureFontStylesheet(url: string): void {
  const head = document.head;
  const styleId = `csw-font-css-${safeToken(url)}`;

  if (!head.querySelector('link[data-csw-preconnect="fonts-googleapis"]')) {
    const preconnectGoogle = document.createElement("link");
    preconnectGoogle.rel = "preconnect";
    preconnectGoogle.href = "https://fonts.googleapis.com";
    preconnectGoogle.setAttribute("data-csw-preconnect", "fonts-googleapis");
    head.appendChild(preconnectGoogle);
  }

  if (!head.querySelector('link[data-csw-preconnect="fonts-gstatic"]')) {
    const preconnectGstatic = document.createElement("link");
    preconnectGstatic.rel = "preconnect";
    preconnectGstatic.href = "https://fonts.gstatic.com";
    preconnectGstatic.crossOrigin = "anonymous";
    preconnectGstatic.setAttribute("data-csw-preconnect", "fonts-gstatic");
    head.appendChild(preconnectGstatic);
  }

  if (!document.getElementById(styleId)) {
    const link = document.createElement("link");
    link.id = styleId;
    link.rel = "stylesheet";
    link.href = url;
    link.setAttribute("data-csw-font", "poppins");
    head.appendChild(link);
  }
}

function resolveDataSource(config: WidgetMountConfig): WidgetDataSource {
  const mode = config.mode ?? "live";

  if (mode === "live") {
    const base = config.apiBase?.trim() ?? "";
    if (base === "") {
      throw new Error("Live mode requires apiBase (WordPress REST base for csafe/v1).");
    }
    return new LiveDataSource(base);
  }

  if (config.dataSource) {
    return config.dataSource;
  }

  throw new Error('Mock mode requires a dataSource (pass { mode: "mock", dataSource: new MockDataSource() }).');
}

function mount(selector: string, config: WidgetMountConfig = { mode: "live" }): void {
  const host = document.querySelector<HTMLElement>(selector);
  if (!host) {
    throw new Error(`Mount target not found for selector: ${selector}`);
  }
  ensureHostFocusResetStyles();

  if (!config.disableDefaultFontLoading) {
    ensureFontStylesheet(config.fontCssUrl ?? DEFAULT_FONT_CSS_URL);
  }

  roots.get(selector)?.unmount();
  roots.delete(selector);
  focusGuards.get(selector)?.();
  focusGuards.delete(selector);

  host.innerHTML = "";
  const shadowRoot = host.shadowRoot ?? host.attachShadow({ mode: "open" });
  shadowRoot.innerHTML = "";

  const tailwindStyle = document.createElement("style");
  tailwindStyle.textContent = tailwindCss;
  shadowRoot.appendChild(tailwindStyle);

  const widgetStyle = document.createElement("style");
  widgetStyle.textContent = widgetCss;
  shadowRoot.appendChild(widgetStyle);

  const mountNode = document.createElement("div");
  mountNode.id = "csafe-widget-root";
  shadowRoot.appendChild(mountNode);

  focusGuards.set(selector, installFocusGuard(shadowRoot));

  const dataSource = resolveDataSource(config);

  const root = createRoot(mountNode);
  root.render(<App dataSource={dataSource} />);
  roots.set(selector, root);
}

function unmount(selector: string): void {
  const root = roots.get(selector);
  root?.unmount();
  roots.delete(selector);
  focusGuards.get(selector)?.();
  focusGuards.delete(selector);
}

window.CSafeParcelWidget = {
  mount,
  unmount
};

export { mount, unmount };
export { LiveDataSource } from "./data/live-data-source";
export type { WidgetDataSource, LoadedQuestions, QuestionGraph } from "./data/types";
